import {
  Avatar,
  Badge,
  SelectionList,
  SelectionListChangeEvent,
} from '@dixa/lib-shared-habla';
import { getAsyncStatusFromRtkQueryStates } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import { useCallback, useEffect, useMemo } from 'react';
import { useMount, useUnmount } from 'react-use';
import { Waypoint } from 'react-waypoint';
import isEmail from 'validator/lib/isEmail';

import { useLazyReadSearchInfiniteQuery } from 'apis/search';
import { appActions } from 'app/app.actions';
import { userModalActions } from 'components/usersModal';
import {
  CommandLauncherAction,
  CommandLauncherScope,
  useCommandLauncher,
} from 'utils/commandLauncher';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { userTypeAssets } from 'utils/user';
import { getUserDisplayName } from 'utils/user/getUserDisplayName';

import styles from '../../commandLauncher.module.scss';
import { getCommandLauncherActionItems } from '../../utils/getCommandLauncherActionSelectionListItems';

const CREATE_CONTACT_ACTION_ID = 'user-create-contact';

/** Checks if the input looks like a phone number. */
const isPhoneNumberLike = (input: string): boolean => {
  // Remove common phone formatting characters and check if mostly digits
  const digits = input.replace(/[\s\-().+]/g, '');
  // At least 6 digits and mostly numeric
  return digits.length >= 6 && /^\d+$/.test(digits);
};

/** Represents the user scope list of the command launcher. */
export const UserScopeList = (): JSX.Element => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();

  const {
    actions: allActions,
    isActive,
    query,
    createAction,
    performAction,
    registerActions,
    setAsyncStatus,
  } = useCommandLauncher();

  useMount(() => ampli.commandPaletteScopeEntered({ scope: 'user' }));

  // Not using scopeQuery since it is truncated at the next '@', which breaks emails.
  const term = query.slice(1).trim();
  const isStrict =
    term.length > 1 && term.startsWith('"') && term.endsWith('"');
  const searchQuery = isStrict ? term.slice(1, -1).trim() : term;

  const [
    readSearchUsers,
    {
      results: users,
      isUninitialized,
      isFetching,
      isSuccess,
      isError,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
    },
  ] = useLazyReadSearchInfiniteQuery<'users'>();

  const [createContactActions, contactActions] = useMemo(() => {
    const scopedActions = allActions.filter((action) =>
      action.scope?.includes(CommandLauncherScope.User),
    );

    return [
      scopedActions.filter(({ id }) => id === CREATE_CONTACT_ACTION_ID),
      scopedActions.filter(({ id }) => id !== CREATE_CONTACT_ACTION_ID),
    ];
  }, [allActions]);

  // Handles registering the static actions.
  useEffect(() => {
    const getPrefillUser = () => {
      if (!searchQuery) return {};

      if (isEmail(searchQuery)) {
        return { email: searchQuery };
      }
      if (isPhoneNumberLike(searchQuery)) {
        return { phone_number: searchQuery };
      }
      return { name: searchQuery };
    };

    const createContactAction: CommandLauncherAction = {
      ...createAction({
        label: searchQuery
          ? `Create new contact "${searchQuery}"`
          : 'Create new contact',
        icon: 'user-plus',
        section: 'Contacts',
        scope: [CommandLauncherScope.User],
        performFn: () => {
          dispatch(userModalActions.show({ user: getPrefillUser() }));
        },
      }),
      id: CREATE_CONTACT_ACTION_ID,
    };

    const unregister = registerActions([createContactAction]);

    return () => unregister();
  }, [createAction, dispatch, registerActions, searchQuery]);

  useEffect(() => {
    const userActions: readonly CommandLauncherAction[] = users.map((user) => {
      const label = getUserDisplayName(user);

      return {
        ...createAction({
          label,
          description: [user.email, user.phone_number]
            .filter((contact) => contact && contact !== label)
            .join('  •  '),
          prefix: <Avatar size="24" user={user} />,
          ...(user.type === 'member' && {
            suffix: (
              <Badge size="small" variant="secondary">
                {userTypeAssets['member'].prettyName}
              </Badge>
            ),
          }),
          section: 'Contacts',
          scope: [CommandLauncherScope.User],
          performFn: () =>
            navigate({
              path: '/contacts/user/:id',
              params: { path: { id: user.id } },
            }),
        }),
        // Stable id keeps list items mounted (and scroll position) when pages are appended.
        id: `user-${user.id}`,
      };
    });

    const unregister = registerActions(userActions);

    return () => unregister();
  }, [createAction, navigate, registerActions, users]);

  useEffect(() => {
    if (searchQuery) {
      readSearchUsers(
        {
          scope: 'users',
          query: searchQuery,
          isStrict,
          pageSize: 50,
        },
        { debounceTime: 300 },
      );
    }
  }, [isStrict, readSearchUsers, searchQuery]);

  useEffect(() => {
    setAsyncStatus(
      getAsyncStatusFromRtkQueryStates({
        isUninitialized,
        isFetching,
        isSuccess,
        isError,
      }),
    );
  }, [isError, isFetching, isSuccess, isUninitialized, setAsyncStatus]);

  useUnmount(() => setAsyncStatus('fulfilled'));

  const handleOnChange = useCallback(
    (event: SelectionListChangeEvent<CommandLauncherAction>) => {
      if (event.selectedItem.isNode) {
        return;
      }

      performAction(event.selectedItem.value, { skipTracking: true });
      dispatch(appActions.hideModal({ type: 'app:commandLauncher' }));
    },
    [performAction, dispatch],
  );

  const emptyStateMessage = !searchQuery
    ? 'Search contacts by name, email or phone number...'
    : isUninitialized || (isFetching && !isFetchingNextPage)
      ? 'Finding matching contacts...'
      : (isSuccess || isError) && !contactActions.length
        ? 'No contacts found. Try a different search term or check the spelling.'
        : undefined;

  // Actions are registered in an effect, so wait for them to catch up with the results.
  const canFetchNextPage =
    !emptyStateMessage &&
    !isFetching &&
    hasNextPage &&
    contactActions.length === users.length;

  return (
    <>
      {emptyStateMessage && (
        <div className={styles.emptyState}>
          <span>{emptyStateMessage}</span>
        </div>
      )}

      <SelectionList isActive={isActive} onChange={handleOnChange}>
        {getCommandLauncherActionItems(
          emptyStateMessage
            ? createContactActions
            : [...createContactActions, ...contactActions],
        )}

        {canFetchNextPage && <Waypoint onEnter={fetchNextPage} />}
      </SelectionList>
    </>
  );
};
